import { compactSmall } from "../../helpers/formatters"
import { css, cx } from "@emotion/css"
import { Loading } from "components/Loading"
import { WidgetScale } from "../Widgets/WidgetScale"
import { WidgetStatsPie } from "../Widgets/WidgetStatsPie"
import { fetchClientView } from "../../api"
import { useI18n } from "providers/I18n"
import { useQuery } from "react-query"
import { useRevenueParams } from "../../hooks/useRevenueParams"
import { useMemo } from "react"
import { EconomicalDataBox } from "./EconomicalDataBox"

export const Client = () => {
  const { t } = useI18n()
  const { search, brandParam, clientParam, isStream } = useRevenueParams()

  const key = useMemo(() => {
    let result = ""
    if (isStream) {
      result = "stream"
    } else if (brandParam === "GCF") {
      result = "gcf"
    } else {
      result = "clients"
    }
    return result
  }, [brandParam, isStream])

  const url = useMemo(() => {
    let result = ""
    if (isStream) {
      result = "client/summary"
    } else if (brandParam === "GCF") {
      result = "client/GCF"
    } else {
      result = `client/${clientParam}/`
    }
    return result
  }, [brandParam, clientParam, isStream])

  const { data, isLoading } = useQuery([key, url, search], fetchClientView)

  if (isLoading) return <Loading />

  const resetScaleWidget = -100

  const npsResetScaleWidget = {
    value: undefined,
    responseRate: undefined,
    delta: undefined,
  }
  return (
    <div className={styles.containerColumn}>
      <EconomicalDataBox data={data} />

      <div>
        <WidgetStatsPie
          currency="$"
          current={data?.labor?.value ?? 0}
          note={t("revenue.directlabor")}
          percentage={data?.labor?.percentage}
          subnote={t("revenue.ofrevenue")}
          className={css`
            color: var(--dark-purple);
          `}
          hasNoDataToShow={data?.labor.percentage === null}
        />
      </div>

      <div>
        <WidgetStatsPie
          currency="$"
          current={data?.cost?.value ?? 0}
          note={t("revenue.directcost")}
          percentage={data?.cost?.percentage}
          subnote={t("revenue.ofrevenue")}
          className={css`
            color: var(--green);
          `}
          hasNoDataToShow={data?.cost.percentage === null}
        />
      </div>

      <div>
        <WidgetStatsPie
          currency="$"
          current={data?.expense?.value ?? 0}
          note={t("revenue.indirectcost")}
          percentage={data?.expense?.percentage}
          subnote={t("revenue.ofrevenue")}
          subnote2={
            data?.labor?.value
              ? compactSmall(
                  data?.labor?.value === null
                    ? 0
                    : (data?.expense?.value ?? 0 * 100) / data?.labor?.value,
                ) + `% ${t("revenue.ofdirectlabor")}`
              : ""
          }
          className={css`
            color: var(--orange);
          `}
          hasNoDataToShow={data?.expense.percentage === null}
        />
      </div>

      <ClientWidgetStats
        value={data?.operatingIncome?.value ?? 0}
        fyrevenue={data?.operatingIncome?.fyrevenue ?? 0}
        growth={data?.operatingIncome?.growth ?? 0}
      />
      {data?.nps?.value && data?.nps && (
        <>
          <WidgetScale
            value={data?.nps?.value ?? resetScaleWidget}
            percentage={data?.nps?.value ?? resetScaleWidget}
            nps={data?.nps ?? npsResetScaleWidget}
            isLoading={isLoading}
            title={clientParam && clientParam !== "All" ? t("revenue.trr") : ""}
          />
        </>
      )}
    </div>
  )
}

const stylesWidgetPieStats = {
  container: css`
    z-index: 2;
    height: 156px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin: var(--spacing-xl) 0;
    padding: 32px;
    border-radius: 20px;
    background-color: var(--grey-opacity);
  `,
  stats: css`
    width: 150px;
    height: 80px;
    flex-grow: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    margin: 0 8px 2px 0;
    padding: 0;
  `,
  title: css`
    width: 65px;
    height: 18px;
    text-align: left;
    color: var(--white);
  `,
  value: css`
    width: 99px;
    height: 44px;
    text-align: left;
    color: var(--white);
  `,
  detail: css`
    text-align: left;
    color: var(--white);
  `,
  line4: css`
    width: 1px;
    height: 48px;
    flex-grow: 0;
    background-color: var(--white);
  `,
}

type ClientWidgetStatsType = {
  value: number
  fyrevenue: number
  growth: number
}
const ClientWidgetStats = (props: ClientWidgetStatsType) => {
  const { t } = useI18n()

  return (
    <>
      <div className={stylesWidgetPieStats.container}>
        <div className={stylesWidgetPieStats.stats}>
          <span className={cx("HeadingH6Regular", stylesWidgetPieStats.title)}>
            {t("revenue.oioi")}
          </span>
          <span className={cx("HeadingH3Black", stylesWidgetPieStats.value)}>
            {props.value ? `$${compactSmall(props.value)}` : "N/A"}
          </span>
        </div>
        <div className={stylesWidgetPieStats.line4}></div>
        <div>
          <span className={cx("HeadingH3Black", stylesWidgetPieStats.value)}>
            {props.growth ? `${compactSmall(props.growth)}%` : "N/A"}
          </span>
        </div>
      </div>
    </>
  )
}

const styles = {
  containerColumn: css`
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: space-between;
  `,
  info: css`
    width: 109px;
    padding: 0;
    margin: 0;
    display: flex;
    flex-grow: 0;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
  `,
  detail: css`
    width: 330px;
    height: 14px;
    margin: 0;
    opacity: 0.6;
    text-align: left;
    white-space: nowrap;
    color: var(--white);
  `,
  title: css`
    width: 554px;
    height: 80px;
    margin-top: 26px;
    padding: 0;
    text-align: left;
    color: var(--white);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `,
  name: css`
    width: 66px;
    height: 18px;
    margin: var(--spacing-xxl-3) 0 0 0;
    text-align: left;
    color: var(--white);
  `,
  amount: css`
    width: 155px;
    height: 80px;
    text-align: left;
    color: var(--white);
  `,
  subdetail: css`
    width: 230px;
    height: 14px;
    margin: 0 0 var(--spacing-xl) 0;
    opacity: 0.6;
    text-align: left;
    color: var(--white);
  `,
}
